import "jspdf-autotable";
import { pdfHeader } from "@/utilities/pdfUtils";

/**
 * Generate teacher attendance summary PDF.
 * @param {Object} options
 * @param {Object} options.settings
 * @param {String} options.locale
 * @param {Function} options.t
 * @param {String} options.period
 * @param {Array} options.teachers
 * @param {Object} options.totals
 * @param {Function} [options.gtag]
 */
export function generateTeacherAttendanceSummaryPdf({
  settings,
  locale,
  t,
  period,
  teachers,
  totals,
  gtag,
}) {
  if (gtag) {
    gtag.event("Imp assiduité enseignants", {
      event_category: "Impression PDF",
      event_label: "Teacher attendance summary",
      value: 1,
    });
  }

  const isArabic = locale === "ar-AR";
  const reverseIfArabic = (arr) => (isArabic ? arr.slice().reverse() : arr);

  const doc = pdfHeader(settings, {
    title: t("teacherAttendance.reportTitle").toUpperCase(),
    subTitle: `${t("teacherAttendance.period")} : ${period}`,
    locale,
  });

  const body = teachers.map((row, index) => {
    const line = [
      index + 1,
      row.teacherName,
      row.present,
      row.absent,
      row.late,
      row.delayMinutes,
      row.workedHours,
      row.workedDays,
      row.offSite,
    ];
    return isArabic ? line.reverse() : line;
  });

  doc.autoTable({
    theme: "grid",
    styles: { font: "Amiri", halign: isArabic ? "right" : "left", fontSize: 9 },
    headStyles: { halign: isArabic ? "right" : "left" },
    bodyStyles: { halign: isArabic ? "right" : "left" },
    startY: 45,
    head: [
      reverseIfArabic([
        t("inscription.num"),
        t("teacherAttendance.teacher"),
        t("assiduite.present"),
        t("assiduite.absence"),
        t("assiduite.retard"),
        t("teacherAttendance.delayMinutes"),
        t("teacherAttendance.workedHours"),
        t("teacherAttendance.workedDays"),
        t("teacherAttendance.offSite"),
      ]),
    ],
    body,
    foot: [
      reverseIfArabic([
        {
          colSpan: 2,
          content: `${t("liste.total")} :`,
          styles: { halign: "center" },
        },
        totals.present,
        totals.absent,
        totals.late,
        totals.delayMinutes,
        totals.workedHours,
        totals.workedDays,
        totals.offSite,
      ]),
    ],
  });

  const filename = `${t("teacherAttendance.reportTitle")} - ${period}.pdf`;
  doc.save(filename);
  return { doc, filename };
}

/**
 * Generate Option 3 (daily / no timetable) attendance summary PDF.
 */
export function generatePersonnelDailyAttendanceSummaryPdf({
  settings,
  locale,
  t,
  title,
  period,
  employees,
  totals,
  gtag,
}) {
  if (gtag) {
    gtag.event("Imp assiduité journalière personnel", {
      event_category: "Impression PDF",
      event_label: "Personnel daily attendance summary",
      value: 1,
    });
  }

  const isArabic = locale === "ar-AR";
  const reverseIfArabic = (arr) => (isArabic ? arr.slice().reverse() : arr);

  const translatedTitle =
    title || t("teacherAttendance.personnelDailyReportTitle");

  const doc = pdfHeader(settings, {
    title: translatedTitle.toUpperCase(),
    subTitle: `${t("teacherAttendance.period")} : ${period}`,
    locale,
  });

  const body = employees.map((row, index) => {
    const line = [
      index + 1,
      row.employeeName,
      row.isA_Teacher
        ? t("personnel.enseignant")
        : t("personnel.personnel"),
      row.present,
      row.absent,
      row.late,
      row.absentMorning,
      row.absentAfternoon,
      row.delayMinutes,
      row.markedDays,
    ];
    return isArabic ? line.reverse() : line;
  });

  doc.autoTable({
    theme: "grid",
    styles: { font: "Amiri", halign: isArabic ? "right" : "left", fontSize: 8 },
    headStyles: { halign: isArabic ? "right" : "left" },
    bodyStyles: { halign: isArabic ? "right" : "left" },
    startY: 45,
    head: [
      reverseIfArabic([
        t("inscription.num"),
        t("etatPaiement.personnel"),
        t("assiduite.type"),
        t("assiduite.present"),
        t("assiduite.absence"),
        t("assiduite.retard"),
        t("teacherAttendance.status_absent_morning_short"),
        t("teacherAttendance.status_absent_afternoon_short"),
        t("teacherAttendance.delayMinutes"),
        t("teacherAttendance.markedDays"),
      ]),
    ],
    body,
    foot: [
      reverseIfArabic([
        {
          colSpan: 3,
          content: `${t("liste.total")} :`,
          styles: { halign: "center" },
        },
        totals.present,
        totals.absent,
        totals.late,
        totals.absentMorning,
        totals.absentAfternoon,
        totals.delayMinutes,
        totals.markedDays,
      ]),
    ],
  });

  const filename = `${translatedTitle} - ${period}.pdf`;
  doc.save(filename);
  return { doc, filename };
}

/**
 * Generate expected vs actual attendance comparison PDF.
 */
export function generateAttendanceComparisonPdf({
  settings,
  locale,
  t,
  title,
  period,
  rows,
  totals,
  gtag,
}) {
  if (gtag) {
    gtag.event("Imp comparaison assiduité", {
      event_category: "Impression PDF",
      event_label: "Attendance expected vs actual",
      value: 1,
    });
  }

  const isArabic = locale === "ar-AR";
  const reverseIfArabic = (arr) => (isArabic ? arr.slice().reverse() : arr);
  const translatedTitle = title || t("teacherAttendance.comparisonTitle");

  const doc = pdfHeader(settings, {
    title: translatedTitle.toUpperCase(),
    subTitle: `${t("teacherAttendance.period")} : ${period}`,
    locale,
  });

  const body = [];
  rows.forEach((row, index) => {
    const line = [
      index + 1,
      row.employeeName,
      row.isA_Teacher
        ? t("personnel.enseignant")
        : t("personnel.personnel"),
      row.expectedHours,
      row.actualHours,
      row.absentHours,
      row.delayHours,
      row.varianceHours,
    ];
    body.push(isArabic ? line.reverse() : line);

    (row.bySubject || []).forEach((subject) => {
      const subjectLine = [
        "",
        `  - ${subject.subjectName || "—"}`,
        t("emploi.matiere"),
        subject.expectedHours,
        subject.actualHours,
        subject.absentHours,
        subject.delayHours,
        subject.varianceHours,
      ];
      body.push(isArabic ? subjectLine.reverse() : subjectLine);
    });
  });

  doc.autoTable({
    theme: "grid",
    styles: { font: "Amiri", halign: isArabic ? "right" : "left", fontSize: 8 },
    headStyles: { halign: isArabic ? "right" : "left" },
    bodyStyles: { halign: isArabic ? "right" : "left" },
    startY: 45,
    head: [
      reverseIfArabic([
        t("inscription.num"),
        t("etatPaiement.personnel"),
        t("assiduite.type"),
        t("teacherAttendance.expectedHours"),
        t("teacherAttendance.actualHours"),
        t("teacherAttendance.absentHours"),
        t("teacherAttendance.delayHours"),
        t("teacherAttendance.varianceHours"),
      ]),
    ],
    body,
    foot: [
      reverseIfArabic([
        {
          colSpan: 3,
          content: `${t("liste.total")} :`,
          styles: { halign: "center" },
        },
        totals.expectedHours,
        totals.actualHours,
        totals.absentHours,
        totals.delayHours,
        totals.varianceHours,
      ]),
    ],
  });

  const filename = `${translatedTitle} - ${period}.pdf`;
  doc.save(filename);
  return { doc, filename };
}
